<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import type { ScheduledAdInfo } from '@/types/classifiedOrder';
import { notifyModalClosed, notifyModalOpened } from '@/utils/iframe';
import ImageLightbox from './ImageLightbox.vue';
import ListingCreativePreview from './ListingCreativePreview.vue';
import ListingDetailModal from './ListingDetailModal.vue';
import { scaleAdFontSizes } from '@/utils/fontScaler';
import { calculatePdfBuilderCardWidth } from '@/utils/pdfBuilderSizing';
import { buildDisplayCustomFields } from '@/utils/customFieldDisplay';
import { useBrowseListingsStore } from '@/stores/browseListings';

const props = defineProps<{
  ad: ScheduledAdInfo;
  categoryLabel?: string;
  batchStart?: boolean;
}>();

const browseStore = useBrowseListingsStore();

const showLightbox = ref(false);
const lightboxInitialIndex = ref(0);
const isExpanded = ref(false);
const showDetailModal = ref(false);
const imageRetryCount = ref(0);
const pdfBuilderNaturalWidth = ref<number | null>(null);
const MAX_IMAGE_RETRIES = 2;

// Reset image state when the composed creative changes.
watch(
  [
    () => props.ad.orderNumber,
    () => props.ad.pdfBuilderImageUrl,
    () => props.ad.pdfBuilderAdCopy,
  ],
  () => {
    imageRetryCount.value = 0;
    pdfBuilderNaturalWidth.value = null;
  },
);

// PDF Builder ad image URL - prefer cached URL from API, fallback to Base64 data URL
// Adds cache-busting parameter to ensure updated images are displayed
const pdfBuilderImageUrl = computed(() => {
  if (props.ad.isPdfBuilderAd) {
    // Prefer cached URL from Azure Blob Storage (when pdfImageUrls=true)
    if (props.ad.pdfBuilderImageUrl) {
      // Add cache-busting parameter using order end date (changes when ad is modified)
      // This allows browser caching within the same order version but busts cache on updates
      const cacheBuster = props.ad.orderEndDate 
        ? new Date(props.ad.orderEndDate).getTime() 
        : Date.now();
      const separator = props.ad.pdfBuilderImageUrl.includes('?') ? '&' : '?';
      return `${props.ad.pdfBuilderImageUrl}${separator}v=${cacheBuster}`;
    }
    // Fallback to Base64 data URL for backward compatibility
    if (props.ad.pdfBuilderAdCopy) {
      return `data:image/jpeg;base64,${props.ad.pdfBuilderAdCopy}`;
    }
  }
  return null;
});

// Primary image to display (PDF Builder image, or first print/web image)
const primaryImage = computed(() => {
  // For PDF Builder ads, use the PDF Builder image
  if (pdfBuilderImageUrl.value) {
    return pdfBuilderImageUrl.value;
  }
  // Otherwise use print images or web files
  if (props.ad.printImages && props.ad.printImages.length > 0) {
    return props.ad.printImages[0];
  }
  if (props.ad.webFiles && props.ad.webFiles.length > 0) {
    return props.ad.webFiles[0];
  }
  return null;
});

// Check if URL is an Azure SAS URL that doesn't support conditional headers
function isAzureSasUrl(url: string): boolean {
  return (url.includes('.blob.core.windows.net') || url.includes('.file.core.windows.net')) 
    && url.includes('sig=');
}

// Image URL with cache-buster to prevent conditional header issues with Azure SAS URLs
const displayImageUrl = computed(() => {
  const base = primaryImage.value;
  if (!base) return null;
  // Don't add cache-buster to data URLs
  if (base.startsWith('data:')) return base;
  
  const separator = base.includes('?') ? '&' : '?';
  
  // For Azure SAS URLs, always add a cache-buster based on order number to prevent
  // the browser from sending conditional headers (If-None-Match, If-Modified-Since)
  // which Azure SAS URLs don't support (returns 400 error)
  if (isAzureSasUrl(base)) {
    // Use order number as a stable cache-buster - same listing = same URL = cached
    // Add retry count if we've had failures
    const stableKey = encodeURIComponent(props.ad.orderNumber);
    const cacheBuster = imageRetryCount.value > 0 
      ? `_cb=${stableKey}_${imageRetryCount.value}`
      : `_cb=${stableKey}`;
    return `${base}${separator}${cacheBuster}`;
  }
  
  // For non-Azure URLs, only add cache-buster on retry
  if (imageRetryCount.value > 0) {
    return `${base}${separator}_retry=${imageRetryCount.value}`;
  }
  return base;
});

const pdfBuilderCardWidth = computed(() => {
  if (!props.ad.isPdfBuilderAd || pdfBuilderNaturalWidth.value === null) {
    return undefined;
  }

  return calculatePdfBuilderCardWidth(
    pdfBuilderNaturalWidth.value,
    props.ad.pdfBuilderBaselineFontSizePt,
    props.ad.pdfBuilderRenderDpi,
  ) ?? undefined;
});

// Combine the composed creative and any attachments for the enlarged carousel.
const allImages = computed(() => {
  const images: string[] = [];

  if (displayImageUrl.value) {
    images.push(displayImageUrl.value);
  }

  const attachments = [
    ...(props.ad.printImages || []),
    ...(props.ad.webFiles || []),
  ];

  for (const image of attachments) {
    if (image !== primaryImage.value && !images.includes(image)) {
      images.push(image);
    }
  }

  return images;
});

// Handle image load failure - retry a few times
function handleImageError() {
  pdfBuilderNaturalWidth.value = null;
  if (imageRetryCount.value < MAX_IMAGE_RETRIES) {
    imageRetryCount.value++;
    console.log(`Image load failed, retry ${imageRetryCount.value}/${MAX_IMAGE_RETRIES}`);
  }
}

// Additional images count (total - 1 for the primary)
const additionalImagesCount = computed(() => {
  return Math.max(0, allImages.value.length - 1);
});

const hasMultipleImages = computed(() => allImages.value.length > 1);

// Check if text is long enough to need truncation
const adText = computed(() => {
  const raw = props.ad.adTextFormatted || props.ad.adTextPlain || '';
  return scaleAdFontSizes(raw);
});
const isLongText = computed(() => (props.ad.adTextPlain?.length || 0) > 300);

// Custom fields for display (limit to 4 on card, truncate values to 25 chars)
const displayCustomFields = computed(() => {
  if (!props.ad.customFieldValues || props.ad.customFieldValues.length === 0) {
    return [];
  }
  
  // Get custom field definitions for this ad's classification
  const fieldDefinitions = browseStore.getCustomFieldsForClassification(props.ad.classificationKey);
  
  return buildDisplayCustomFields(
    props.ad.customFieldValues,
    fieldDefinitions,
    25, // maxDisplayLength for chips
    4   // limit to 4 fields on card
  );
});

function openDetailModal() {
  notifyModalOpened();
  showDetailModal.value = true;
}

function closeDetailModal() {
  if (!showDetailModal.value) return;
  notifyModalClosed();
  showDetailModal.value = false;
}

function openLightbox(index = 0) {
  lightboxInitialIndex.value = index;
  notifyModalOpened();
  showLightbox.value = true;
}

function closeLightbox() {
  if (!showLightbox.value) return;
  notifyModalClosed();
  showLightbox.value = false;
}

function handleCardClick() {
  if (!props.ad.isPdfBuilderAd) {
    openDetailModal();
  }
}

function handlePdfBuilderImageLoad(event: Event) {
  const image = event.target as HTMLImageElement;
  pdfBuilderNaturalWidth.value = image.naturalWidth > 0 ? image.naturalWidth : null;
}

function toggleExpanded(event: Event) {
  // Prevent opening the detail modal when clicking "Read more"
  event.stopPropagation();
  isExpanded.value = !isExpanded.value;
}

onUnmounted(() => {
  if (showDetailModal.value || showLightbox.value) {
    notifyModalClosed();
  }
});

</script>

<template>
  <article 
    class="listing-card" 
    :class="{
      'listing-card--creative': ad.isPdfBuilderAd,
      'category-batch-start': batchStart
    }"
    @click="handleCardClick"
  >
    <template v-if="ad.isPdfBuilderAd && primaryImage">
      <ListingCreativePreview
        :image-url="displayImageUrl || ''"
        image-alt="Classified display listing"
        :category-label="categoryLabel"
        :additional-image-count="additionalImagesCount"
        :display-width-px="pdfBuilderCardWidth"
        loading="lazy"
        @activate="openLightbox(0)"
        @load="handlePdfBuilderImageLoad"
        @error="handleImageError"
      />
    </template>

    <template v-else>
      <div v-if="categoryLabel" class="category-marker">
        {{ categoryLabel }}
      </div>

      <!-- Image section for regular classified listings -->
      <div v-if="primaryImage" class="listing-image-section">
        <div class="listing-image-container">
          <img
            :src="displayImageUrl || undefined"
            alt="Listing image"
            class="listing-image"
            @error="handleImageError"
          />
          <div v-if="hasMultipleImages" class="listing-image-badge">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            +{{ additionalImagesCount }}
          </div>
        </div>
      </div>
    </template>
    
    <!-- Text content (hidden for PDF Builder ads since the image contains everything) -->
    <div v-if="!ad.isPdfBuilderAd" class="listing-content">
      <div 
        class="listing-text" 
        :class="{ 'listing-text--truncated': isLongText && !isExpanded }"
        v-html="adText"
      ></div>
      <button 
        v-if="isLongText" 
        class="read-more-btn"
        @click="toggleExpanded"
      >
        {{ isExpanded ? 'Show less' : 'Read more' }}
      </button>
    </div>
    
    <!-- Custom field chips -->
    <div v-if="displayCustomFields.length > 0" class="custom-field-chips">
      <span 
        v-for="cf in displayCustomFields" 
        :key="cf.key" 
        class="custom-field-chip"
      >
        <span class="chip-name">{{ cf.name }}:</span>
        <span class="chip-value">{{ cf.displayValue }}</span>
      </span>
    </div>
  </article>
  
  <!-- Detail Modal for larger view -->
  <ListingDetailModal
    v-if="showDetailModal"
    :ad="ad"
    @close="closeDetailModal"
  />
  
  <!-- Keep lightbox for backwards compatibility if needed -->
  <ImageLightbox
    v-if="ad.isPdfBuilderAd && allImages.length > 0"
    :images="allImages"
    :is-open="showLightbox"
    :initial-index="lightboxInitialIndex"
    :copyable-text-html="adText"
    @close="closeLightbox"
  />
</template>

<style scoped>
.listing-card {
  position: relative;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  cursor: pointer;
}

.listing-card.category-batch-start {
  margin-top: 10px;
}

.listing-card--creative {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
  cursor: default;
}

.category-marker {
  position: relative;
  z-index: 2;
  display: block;
  width: fit-content;
  max-width: calc(100% - var(--spacing-4));
  padding: 0.3rem 0.65rem;
  overflow: hidden;
  color: var(--accent-fill-text-color);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--accent-primary);
  border-right: 1px solid var(--accent-primary);
  border-bottom: 1px solid var(--accent-primary);
  border-bottom-right-radius: var(--radius-sm);
}

/* Desktop hover effect - zoom with elevation */
@media (hover: hover) {
  .listing-card:not(.listing-card--creative):hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
    transform: scale(1.25);
    z-index: 100;
    position: relative;
  }
}

.listing-image-section {
  flex-shrink: 0;
}

.listing-image-container {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background: var(--bg-tertiary);
}

.listing-image {
  width: 100%;
  height: auto; /* Natural height - no cropping */
  max-height: 300px; /* Reasonable max to prevent overly tall images */
  object-fit: contain; /* Show full image without cropping */
  image-orientation: from-image; /* Respect EXIF rotation data */
  display: block;
  transition: transform 0.3s ease;
}

.listing-image-container:hover .listing-image {
  transform: scale(1.05);
}

.listing-image-badge {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(4px);
}

.listing-content {
  padding: var(--spacing-4);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.listing-text {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--text-primary);
  word-wrap: break-word;
  overflow-wrap: break-word;
  flex: 1;
}

.listing-text--truncated {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.read-more-btn {
  background: none;
  border: none;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0;
  margin-top: var(--spacing-2);
  cursor: pointer;
  text-align: left;
  transition: color 0.2s;
}

.read-more-btn:hover {
  color: var(--accent-secondary);
  text-decoration: underline;
}

/* Style any HTML content that comes from adTextFormatted */
.listing-text :deep(p) {
  margin: 0 0 0.75em 0;
}

.listing-text :deep(p:last-child) {
  margin-bottom: 0;
}

.listing-text :deep(strong),
.listing-text :deep(b) {
  font-weight: 600;
  color: var(--text-primary);
}

.listing-text :deep(em),
.listing-text :deep(i) {
  font-style: italic;
}

.listing-text :deep(br) {
  content: '';
  display: block;
  margin-top: 0.5em;
}

/* Custom field chips */
.custom-field-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px var(--spacing-4);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.custom-field-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
}

.chip-name {
  color: var(--text-tertiary);
  font-weight: 500;
}

.chip-value {
  color: var(--text-primary);
}

@media (max-width: 480px) {
  .listing-image {
    height: 150px;
  }
  
  .listing-content {
    padding: var(--spacing-3);
  }
  
  .listing-text {
    font-size: 0.875rem;
    line-height: 1.6;
  }
}
</style>
